/**
 * SharePointFiles — file upload/download against a SharePoint folder.
 * Obtain instances via `spClient.files({ rootPath })`.
 */

export class SharePointFiles {
  /**
   * @param {object} options
   * @param {object} options.spClient - SharePoint client instance (required)
   * @param {string} [options.rootPath=''] - SharePoint folder root path
   * @param {boolean} [options.defaultToLocal=false] - On error, fall back to
   *   browser-side download/file-picker instead of throwing.
   */
  constructor(options) {
    if (!options?.spClient) {
      throw new Error('SharePointFiles: spClient is required');
    }
    this.spClient = options.spClient;
    this.rootPath = options.rootPath ?? '';
    this.defaultToLocal = options.defaultToLocal ?? false;
  }

  /**
   * Uploads a file to SharePoint.
   * @param {string} filename
   * @param {string} data - File content as string
   * @returns {Promise<object>}
   */
  async uploadFile(filename, data) {
    if (!filename || data === undefined) {
      throw new Error(
        'uploadFile: Missing required parameters (filename, data)'
      );
    }

    try {
      const arrayBuffer = new TextEncoder().encode(data);
      return await this.spClient.request(
        `web/getfolderbyserverrelativeurl('${this.rootPath}')/files/add(overwrite=true,url='${encodeURIComponent(filename)}')`,
        'POST',
        {
          body: arrayBuffer,
          headers: { 'Content-Type': 'application/octet-stream' },
          batch: false,
        },
        true
      );
    } catch (error) {
      if (this.defaultToLocal) {
        console.warn(
          `Failed to upload '${filename}' to SharePoint, falling back to local download:`,
          error.message
        );
        return this._downloadToLocal(data, filename);
      }
      throw error;
    }
  }

  /**
   * Downloads a file from SharePoint.
   * @param {string} filename
   * @returns {Promise<string>}
   */
  async downloadFile(filename) {
    if (!filename) {
      throw new Error('downloadFile: Missing required parameter (filename)');
    }

    try {
      const result = await this.spClient.request(
        `web/getfolderbyserverrelativeurl('${this.rootPath}')/files('${encodeURIComponent(filename)}')/$value`,
        'GET',
        { parseAsText: true, batch: false }
      );
      return result.textResponse;
    } catch (error) {
      if (this.defaultToLocal) {
        console.warn(
          `Failed to download '${filename}' from SharePoint, falling back to local file picker:`,
          error.message
        );
        return this._selectFromLocal();
      }
      throw error;
    }
  }

  /**
   * Uploads an object as a JSON .conf file.
   * @param {string} filename - Base filename (without extension)
   * @param {object} data
   * @returns {Promise<object>}
   */
  async uploadJson(filename, data) {
    if (!filename || data === undefined) {
      throw new Error(
        'uploadJson: Missing required parameters (filename, data)'
      );
    }
    const confFilename = filename.endsWith('.conf')
      ? filename
      : `${filename}.conf`;
    return this.uploadFile(confFilename, JSON.stringify(data, null, 2));
  }

  /**
   * Downloads a .conf file and parses it as JSON.
   * @param {string} filename - Base filename (without extension)
   * @returns {Promise<object>}
   */
  async downloadJson(filename) {
    if (!filename) {
      throw new Error('downloadJson: Missing required parameter (filename)');
    }
    const confFilename = filename.endsWith('.conf')
      ? filename
      : `${filename}.conf`;
    const text = await this.downloadFile(confFilename);
    return JSON.parse(text);
  }

  /**
   * Triggers a local file download.
   * @param data
   * @param filename
   */
  _downloadToLocal(data, filename) {
    return new Promise(resolve => {
      const blob = new Blob([data]);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      resolve({ success: true, method: 'local_download', filename });
    });
  }

  /** Triggers a local file selection dialog. */
  _selectFromLocal() {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '*/*';
      input.onchange = event => {
        const file = event.target.files[0];
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }
        const reader = new FileReader();
        reader.onload = e => resolve(e.target.result);
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsText(file);
      };
      input.oncancel = () => reject(new Error('File selection cancelled'));
      input.click();
    });
  }

  /**
   * Gets the ETag of a file via direct HEAD (no /_api/, no digest).
   * @param {string} filename
   * @returns {Promise<string|null>} ETag, or null if the file doesn't exist
   */
  async getETag(filename) {
    if (!filename) {
      throw new Error('getETag: Missing required parameter (filename)');
    }
    const fileUrl = `${this.spClient.siteUrl}/${this.rootPath}/${filename}`;
    const response = await fetch(fileUrl, {
      method: 'HEAD',
      headers: { Accept: 'application/json;odata=verbose' },
      credentials: 'include',
    });
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new Error(`HEAD request failed: Status ${response.status}`);
    }
    return response.headers.get('ETag');
  }

  /**
   * Downloads a file with its ETag using a direct GET. Supports conditional
   * GET via `ifNoneMatch`: a 304 returns `{data: null, etag, notModified: true}`.
   *
   * @param {string} filename
   * @param {object} [options]
   * @param {string|null} [options.ifNoneMatch]
   * @returns {Promise<{data: string|null, etag: string|null, notModified?: boolean}>}
   */
  async downloadWithETag(filename, options = {}) {
    if (!filename) {
      throw new Error(
        'downloadWithETag: Missing required parameter (filename)'
      );
    }

    const { ifNoneMatch } = options;
    const fileUrl = `${this.spClient.siteUrl}/${this.rootPath}/${filename}`;
    const headers = { Accept: 'application/json;odata=verbose' };
    if (ifNoneMatch) headers['If-None-Match'] = ifNoneMatch;

    try {
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers,
        credentials: 'include',
      });
      if (response.status === 304) {
        return { data: null, etag: ifNoneMatch, notModified: true };
      }
      if (!response.ok) {
        throw new Error(`GET request failed: Status ${response.status}`);
      }
      return {
        data: await response.text(),
        etag: response.headers.get('ETag'),
      };
    } catch (error) {
      if (this.defaultToLocal) {
        console.warn(
          `Failed to download '${filename}', falling back to local file picker:`,
          error.message
        );
        return { data: await this._selectFromLocal(), etag: null };
      }
      throw error;
    }
  }

  /**
   * Subscribe to changes of a remote file.
   *
   * Performs conditional GETs (If-None-Match) on a hybrid trigger schedule:
   * a safety-net interval timer, the document `visibilitychange` → visible,
   * and the window `online` event. The callback fires only when the body
   * actually changes (HTTP 200 with a fresh ETag); 304 responses do not
   * trigger it.
   *
   * @param {string} filename
   * @param {(payload: {data: string, etag: string|null}) => void} callback
   * @param {object} [options]
   * @param {number} [options.intervalMs=1800000] - Polling interval (default 30 min). 0 disables only the timer.
   * @param {boolean} [options.onVisibility=true]
   * @param {boolean} [options.onOnline=true]
   * @returns {() => void} Unsubscribe function
   */
  onFileChange(filename, callback, options = {}) {
    if (!filename) {
      throw new Error('onFileChange: Missing required parameter (filename)');
    }
    if (typeof callback !== 'function') {
      throw new Error('onFileChange: callback must be a function');
    }

    const intervalMs = options.intervalMs ?? 30 * 60 * 1000;
    const onVisibility = options.onVisibility ?? true;
    const onOnline = options.onOnline ?? true;

    let lastEtag = null;
    let stopped = false;
    let inFlight = false;

    const tick = async () => {
      if (stopped || inFlight) return;
      inFlight = true;
      try {
        const result = await this.downloadWithETag(filename, {
          ifNoneMatch: lastEtag,
        });
        if (stopped || result.notModified) return;
        lastEtag = result.etag ?? lastEtag;
        callback({ data: result.data, etag: result.etag ?? null });
      } catch (error) {
        console.warn(
          `onFileChange: tick failed for '${filename}':`,
          error.message
        );
      } finally {
        inFlight = false;
      }
    };

    const timerId = intervalMs > 0 ? setInterval(tick, intervalMs) : null;

    let visibilityHandler = null;
    if (onVisibility && typeof document !== 'undefined') {
      visibilityHandler = () => {
        if (document.visibilityState === 'visible') tick();
      };
      document.addEventListener('visibilitychange', visibilityHandler);
    }

    let onlineHandler = null;
    if (onOnline && typeof window !== 'undefined') {
      onlineHandler = () => tick();
      window.addEventListener('online', onlineHandler);
    }

    tick();

    return () => {
      stopped = true;
      if (timerId !== null) clearInterval(timerId);
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      if (onlineHandler) {
        window.removeEventListener('online', onlineHandler);
      }
    };
  }
}
